// src/context/ProductContext.tsx
"use client";

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Prisma, ProductStatus } from '@prisma/client';
import { toast } from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  createProduct as createProductService,
  getProduct as getProductService,
  updateProduct as updateProductService,
  deleteProduct as deleteProductService,
  getProducts as getProductsService,
  updateProductStatus as updateProductStatusService,
} from '../services/auth';
import { useAuth } from './AuthContext';
type Product = Prisma.ProductGetPayload<{
  include: {
    images: true,
    attributes: {
      include: {
        values: true;
      }
    }
    variants: {
      include: {
        images: true,
        attributes: {
          include: {
            values: true;
          }
        }
      };
    }
  };
}>;
interface ProductContextType {
  products: Product[];
  fetchProducts: (
    page?: number,
    limit?: number,
    searchTerm?: string,
    selectedCategory?: string,
    sortBy?: string,
    priceRange?: [number, number]
  ) => Promise<void>;
  createProduct: (product: Partial<Product>) => Promise<void>;
  getProduct: (id: string) => Promise<Product | null>;
  updateProduct: (id: string, product: Partial<Product>) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  updateProductStatus: (productId: string, newStatus: ProductStatus) => Promise<void>;
  currentPage: number;
  hasNextPage: boolean;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider = ({ children }: { children: ReactNode }) => {
  const productContext = useProvideProduct();
  return <ProductContext.Provider value={productContext}>{children}</ProductContext.Provider>;
};

export const useProduct = (): ProductContextType => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
};

const useProvideProduct = (): ProductContextType => {
  const [products, setProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const handleError = useCallback((error: unknown, defaultMessage: string) => {
    let message = defaultMessage;

    if (error instanceof AxiosError) {
      message = error.response?.data?.message || defaultMessage;
    } else if (error instanceof Error) {
      message = error.message;
    }

    console.error('Product Error:', message);
    toast.error(message, {
      style: {
        border: '1px solid #713200',
        padding: '16px',
        color: '#713200',
      },
      iconTheme: {
        primary: '#713200',
        secondary: '#FFFAEE',
      },
    });
  }, []);

  const fetchProducts = useCallback(
    async (
      page?: number,
      limit?: number,
      searchTerm?: string,
      selectedCategory?: string,
      sortBy?: string,
      priceRange?: [number, number]
    ) => {
      try {
        const response = await getProductsService(
          page,
          limit,
          searchTerm,
          selectedCategory,
          sortBy,
          priceRange,
        );
        setProducts(response.products);
        setHasNextPage(response.products.length === limit);
        if (page) {
          setCurrentPage(page);
        }
      } catch (error: unknown) {
        handleError(error, 'Неуспешно зареждане на продуктите.');
      }
    },
    [handleError]
  );

  const createProduct = useCallback(
    async (product: Partial<Product>) => {
      try {
        await createProductService(product);
        toast.success('Продуктът е създаден успешно.', {
          style: {
            border: '1px solid #713200',
            padding: '16px',
            color: '#713200',
          },
          iconTheme: {
            primary: '#713200',
            secondary: '#FFFAEE',
          },
        });
        // Optionally, refetch products or update state
      } catch (error: unknown) {
        handleError(error, 'Неуспешно създаване на продукта.');
      }
    },
    [handleError]
  );

  const getProduct = useCallback(
    async (id: string): Promise<Product | null> => {
      try {
        const response = await getProductService(id);
        return response || null;
      } catch (error: unknown) {
        handleError(error, 'Неуспешно зареждане на продукта.');
        return null;
      }
    },
    [handleError]
  );

  const updateProduct = useCallback(
    async (id: string, product: Partial<Product>) => {
      try {
        await updateProductService(id, product);
        toast.success('Продуктът е актуализиран успешно.', {
          style: {
            border: '1px solid #713200',
            padding: '16px',
            color: '#713200',
          },
          iconTheme: {
            primary: '#713200',
            secondary: '#FFFAEE',
          },
        });
        // Optionally, refetch products or update state
      } catch (error: unknown) {
        handleError(error, 'Неуспешно актуализиране на продукта.');
      }
    },
    [handleError]
  );

  const deleteProduct = useCallback(
    async (id: string) => {
      try {
        await deleteProductService(id);
        toast.success('Продуктът е изтрит успешно.', {
          style: {
            border: '1px solid #713200',
            padding: '16px',
            color: '#713200',
          },
          iconTheme: {
            primary: '#713200',
            secondary: '#FFFAEE',
          },
        });
        // Optionally, refetch products or update state
      } catch (error: unknown) {
        handleError(error, 'Неуспешно изтриване на продукта.');
      }
    },
    [handleError]
  );

  const updateProductStatus = useCallback(
    async (productId: string, newStatus: ProductStatus) => {
      try {
        await updateProductStatusService(productId, newStatus);
        toast.success(`Product status updated to ${newStatus}.`, {
          style: {
            border: '1px solid #713200',
            padding: '16px',
            color: '#713200',
          },
          iconTheme: {
            primary: '#713200',
            secondary: '#FFFAEE',
          },
        });
        // Optionally, refetch products or update state
        fetchProducts(currentPage); // Refresh the current page
      } catch (error: unknown) {
        handleError(error, 'Неуспешно актуализиране на статуса на продукта.');
      }
    },
    [handleError, fetchProducts, currentPage]
  );

  return {
    products,
    fetchProducts,
    createProduct,
    getProduct,
    updateProduct,
    deleteProduct,
    updateProductStatus,
    currentPage,
    hasNextPage,
  };
};

export default ProductProvider;
